import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { StatusBadge } from '../../../shared/components/status-badge.component';
import { ExpenseRequestExportService } from '../services/expense-request-export.service';
import {
  ExpenseRequestBookingType,
  ExpenseRequestItem,
  ExpenseRequestService,
  ExpenseRequestStatus,
  ExpenseRequestTargetType,
} from '../services/expense-request.service';

const currentMonth = (() => {
  const today = new Date();
  return {
    from: new Date(today.getFullYear(), today.getMonth(), 1),
    to: new Date(today.getFullYear(), today.getMonth() + 1, 0),
  };
})();

function toDateFilter(date: Date | null): string {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

@Component({
  selector: 'app-expense-requests-page',
  imports: [
    DatePipe,
    DecimalPipe,
    FormsModule,
    LucideAngularModule,
    DatePickerModule,
    InputTextModule,
    SelectModule,
    TableModule,
    StatusBadge,
  ],
  templateUrl: './expense-requests-page.component.html',
})
export class ExpenseRequestsPageComponent implements OnInit {
  private readonly overlay = inject(GlobalOverlayService);
  private readonly router = inject(Router);
  private readonly expenseRequestService = inject(ExpenseRequestService);
  private readonly exportService = inject(ExpenseRequestExportService);

  readonly items = signal<ExpenseRequestItem[]>([]);
  readonly searchTerm = signal('');
  readonly dateFrom = signal(toDateFilter(currentMonth.from));
  readonly dateTo = signal(toDateFilter(currentMonth.to));
  readonly status = signal<'ALL' | ExpenseRequestStatus>('ALL');
  readonly bookingType = signal<'ALL' | ExpenseRequestBookingType>('ALL');
  readonly targetType = signal<'ALL' | ExpenseRequestTargetType>('ALL');
  readonly expenseTypeId = signal('ALL');
  readonly wholesaleId = signal('ALL');

  readonly pendingDateFrom = signal<Date | null>(new Date(currentMonth.from));
  readonly pendingDateTo = signal<Date | null>(new Date(currentMonth.to));
  readonly pendingStatus = signal<'ALL' | ExpenseRequestStatus>('ALL');
  readonly pendingBookingType = signal<'ALL' | ExpenseRequestBookingType>('ALL');
  readonly pendingTargetType = signal<'ALL' | ExpenseRequestTargetType>('ALL');
  readonly pendingExpenseTypeId = signal('ALL');
  readonly pendingWholesaleId = signal('ALL');

  readonly statusOptions: Array<{
    label: string;
    value: 'ALL' | ExpenseRequestStatus;
  }> = [
    { label: 'ทุกสถานะ', value: 'ALL' },
    { label: 'รอหัวหน้าอนุมัติ', value: 'SUPERVISOR_REVIEW' },
    { label: 'รอบัญชีจ่ายเงิน', value: 'WAITING_PAYMENT' },
    { label: 'รอใบเสร็จ', value: 'WAITING_CLEARING' },
    { label: 'รอบัญชีตรวจใบเสร็จ', value: 'ACCOUNTING_REVIEW_RECEIPT' },
    { label: 'ตรวจสอบแล้ว', value: 'VERIFIED' },
    { label: 'คืนเงินแล้ว', value: 'PAID' },
    { label: 'ไม่ผ่าน', value: 'REJECTED' },
    { label: 'ยกเลิก', value: 'CANCELLED' },
  ];

  readonly bookingTypeOptions: Array<{
    label: string;
    value: 'ALL' | ExpenseRequestBookingType;
  }> = [
    { label: 'ทุกประเภท', value: 'ALL' },
    { label: 'จอยทัวร์', value: 'JOIN_TOUR' },
    { label: 'เหมากรุ๊ป', value: 'GROUP_TOUR' },
  ];
  readonly targetTypeOptions: Array<{ label: string; value: 'ALL' | ExpenseRequestTargetType }> = [
    { label: 'ทุกประเภทรายการ', value: 'ALL' },
    { label: 'Booking', value: 'BOOKING' },
    { label: 'Tour', value: 'TOUR' },
    { label: 'ค่าใช้จ่ายทั่วไป', value: 'GENERAL' },
    { label: 'คืนเงินลูกค้า', value: 'REFUND' },
  ];

  readonly expenseTypeOptions = signal<Array<{ label: string; value: string }>>([
    { label: 'ทุกประเภทค่าใช้จ่าย', value: 'ALL' },
  ]);

  readonly wholesaleOptions = signal<Array<{ label: string; value: string }>>([
    { label: 'ทุกรายการโฮลเซล', value: 'ALL' },
    { label: 'ไม่มีโฮลเซล', value: 'NONE' },
  ]);

  readonly filteredItems = computed(() => {
    const query = this.searchTerm().trim().toLocaleLowerCase('th');
    if (!query) return this.items();

    return this.items().filter((item) =>
      [
        item.expenseNo,
        item.targetType,
        item.bookingNo,
        item.tourNo,
        item.customerName,
        item.tourCode,
        item.tourName,
        item.country,
        item.expenseNameSnapshot,
        item.wholesaleNameSnapshot,
        item.description,
        item.requestedByName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase('th')
        .includes(query),
    );
  });

  ngOnInit(): void {
    void this.loadItems();
  }

  async applyFilters(): Promise<void> {
    this.dateFrom.set(toDateFilter(this.pendingDateFrom()));
    this.dateTo.set(toDateFilter(this.pendingDateTo()));
    this.status.set(this.pendingStatus());
    this.bookingType.set(this.pendingBookingType());
    this.targetType.set(this.pendingTargetType());
    this.expenseTypeId.set(this.pendingExpenseTypeId());
    this.wholesaleId.set(this.pendingWholesaleId());
    await this.loadItems();
  }

  async resetFilters(): Promise<void> {
    this.searchTerm.set('');
    this.pendingDateFrom.set(new Date(currentMonth.from));
    this.pendingDateTo.set(new Date(currentMonth.to));
    this.pendingStatus.set('ALL');
    this.pendingBookingType.set('ALL');
    this.pendingTargetType.set('ALL');
    this.pendingExpenseTypeId.set('ALL');
    this.pendingWholesaleId.set('ALL');
    await this.applyFilters();
  }

  bookingTypeLabel(bookingType: ExpenseRequestBookingType | null): string {
    if (!bookingType) return '—';
    return bookingType === 'JOIN_TOUR' ? 'จอยทัวร์' : 'เหมากรุ๊ป';
  }

  targetTypeLabel(targetType: ExpenseRequestTargetType): string {
    return targetType === 'BOOKING'
      ? 'Booking'
      : targetType === 'TOUR'
        ? 'Tour'
        : targetType === 'REFUND'
          ? 'คืนเงินลูกค้า'
          : 'ค่าใช้จ่ายทั่วไป';
  }

  async exportExcel(): Promise<void> {
    const items = this.filteredItems();
    if (items.length === 0) {
      this.overlay.error('ไม่สามารถ Export ได้', 'ไม่พบรายการเบิกหรือคืนเงินตามตัวกรองปัจจุบัน');
      return;
    }

    this.overlay.showLoading('กำลังสร้างไฟล์ Excel...');
    try {
      await this.exportService.export(items, {
        dateFrom: this.dateFrom(),
        dateTo: this.dateTo(),
        status: this.optionLabel(this.statusOptions, this.status()),
        bookingType: this.optionLabel(this.bookingTypeOptions, this.bookingType()),
        targetType: this.optionLabel(this.targetTypeOptions, this.targetType()),
        expenseType: this.optionLabel(this.expenseTypeOptions(), this.expenseTypeId()),
        wholesale: this.optionLabel(this.wholesaleOptions(), this.wholesaleId()),
        searchTerm: this.searchTerm().trim(),
      });
    } catch {
      this.overlay.error('สร้างไฟล์ Excel ไม่สำเร็จ', 'กรุณาลองใหม่อีกครั้ง');
    } finally {
      this.overlay.hideLoading();
    }
  }

  previewDocument(_label: string, path: string): void {
    window.open(
      this.expenseRequestService.resolveFileUrl(path),
      '_blank',
      'noopener,noreferrer',
    );
  }

  viewExpense(item: ExpenseRequestItem): void {
    if ((item.targetType === 'BOOKING' || item.targetType === 'REFUND') && item.bookingId) {
      void this.router.navigate(['/bookings', item.bookingId]);
      return;
    }
    if (item.targetType === 'TOUR' && item.tourId) {
      void this.router.navigate(['/tours', item.tourId]);
    }
  }

  private async loadItems(): Promise<void> {
    this.overlay.showLoading('กำลังโหลดรายการเบิกและคืนเงิน...');
    try {
      const response = await this.expenseRequestService.getItems({
        dateFrom: this.dateFrom(),
        dateTo: this.dateTo(),
        status: this.status(),
        bookingType: this.bookingType(),
        targetType: this.targetType(),
        expenseTypeId: this.expenseTypeId(),
        wholesaleId: this.wholesaleId(),
      });

      this.items.set(response.items);
      this.expenseTypeOptions.set([
        { label: 'ทุกประเภทค่าใช้จ่าย', value: 'ALL' },
        ...response.filterOptions.expenseTypes.map((item) => ({
          label: item.name,
          value: item.id,
        })),
      ]);
      this.wholesaleOptions.set([
        { label: 'ทุกรายการโฮลเซล', value: 'ALL' },
        { label: 'ไม่มีโฮลเซล', value: 'NONE' },
        ...response.filterOptions.wholesales.map((item) => ({
          label: item.name,
          value: item.id,
        })),
      ]);
    } catch (error: unknown) {
      this.overlay.error('โหลดรายการเบิกและคืนเงินไม่สำเร็จ', this.errorMessage(error));
    } finally {
      this.overlay.hideLoading();
    }
  }

  private errorMessage(error: unknown): string {
    return error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
      ? error.error.message
      : 'กรุณาลองใหม่อีกครั้ง';
  }

  private optionLabel<T extends string>(
    options: Array<{ label: string; value: T }>,
    value: T,
  ): string {
    return options.find((option) => option.value === value)?.label ?? value;
  }
}
